/**
 * Tiers List Block - Frontend View
 *
 * Client-side rendering for all enrollment states.
 * Shows skeleton while loading, then renders appropriate state.
 */
import React, { useState, useEffect } from 'react';
import { createRoot } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { useEnrollment } from '@blocks/_shared/hooks/use-enrollment';
import { EarnRulesGridSkeleton } from '@blocks/_shared/skeleton/patterns/earn-rules';
import Button from '@blocks/_shared/button';
import { joinProgram } from '~/shared/queries';
import {
	Item,
	ItemContent,
	ItemMedia,
	ItemLabel,
	ItemMeta,
	ItemBadge,
	ItemActions,
	ItemError,
} from '@blocks/_shared/components';
import { TiersListProvider, useTiersListContext } from './context';
import './style.scss';

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	cardLabelLevel: number;
	buttonTextJoin: string;
	buttonTextSignIn: string;
	buttonTextCurrent: string;
	showDescription: boolean;
	imageHeight: number;
	imageFit: 'contain' | 'cover' | 'fill';
	// Simplified button colors
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
	currentTierBorderColor?: string;
}

interface Tier {
	id: string;
	name: string;
	position: number;
	media: {
		type: string;
		value: string;
	} | null;
	description?: string | null;
}

// ============================================================================
// Main Component
// ============================================================================

const TiersListView = ({ attributes }: { attributes: Attributes }) => {
	const { status, tier: currentTier, creditsCurrency, isLoading: isEnrollmentLoading, refetch } = useEnrollment();
	const [tiers, setTiers] = useState<Tier[]>([]);
	const [isLoadingTiers, setIsLoadingTiers] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Fetch tiers from API - only for enrolled users
	// Guests and non-enrolled users will see the empty state with sign-in/join button
	useEffect(() => {
		// Only fetch tiers for enrolled users
		if (status === 'enrolled') {
			apiFetch<{ tiers: Tier[] }>({ path: '/leat/v1/tiers' })
				.then((response) => {
					// Sort by position
					const sortedTiers = response.tiers.sort((a, b) => a.position - b.position);
					setTiers(sortedTiers);
				})
				.catch((err) => {
					// Real error - show error message
					console.error('[Tiers List] API Error:', err);
					setError(err.message || 'Failed to load tiers');
				})
				.finally(() => setIsLoadingTiers(false));
		} else {
			// For guests and non-enrolled, skip API call
			setIsLoadingTiers(false);
		}
	}, [status]);

	// Show skeleton while loading
	if (isEnrollmentLoading || (status === 'enrolled' && isLoadingTiers)) {
		return <EarnRulesGridSkeleton columns={attributes.columns} />;
	}

	// Show error state
	if (error) {
		return (
			<div className="leat-tiers-list__error">
				<p>{error}</p>
				<Button onClick={() => window.location.reload()} variant="contained">
					Try Again
				</Button>
			</div>
		);
	}

	// Render based on enrollment status
	switch (status) {
		case 'guest':
			// Always show sign-in prompt for guests
			return <GuestState attributes={attributes} />;

		case 'loggedInNotEnrolled':
			// Show join program prompt for non-enrolled users
			return <LoggedInNotEnrolledState attributes={attributes} />;

		case 'enrolled':
			return (
				<EnrolledState
					attributes={attributes}
					tiers={tiers}
					currentTierId={currentTier?.id || null}
					creditsCurrency={creditsCurrency || 'points'}
					isGuest={false}
				/>
			);

		default:
			return null;
	}
};

// ============================================================================
// State Components
// ============================================================================

const GuestState = ({ attributes }: { attributes: Attributes }) => {
	// Use WooCommerce my-account page with redirect back to current page
	const myAccountUrl = window.leat?.storePages?.myaccount?.permalink || '/my-account/';
	const redirectUrl = `${myAccountUrl}?redirect_to=${encodeURIComponent(window.location.href)}`;

	return (
		<div className="leat-tiers-list__empty-state">
			<p>Sign in to view our loyalty tiers and exclusive benefits.</p>
			<Button href={redirectUrl} variant="contained">
				{attributes.buttonTextSignIn}
			</Button>
		</div>
	);
};

const LoggedInNotEnrolledState = ({ attributes }: { attributes: Attributes }) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			// Get current user ID from window.leat
			const userId = window.leat?.userId ?? null;

			if (!userId) {
				throw new Error('User ID not found');
			}

			await joinProgram(userId);

			// Reload the page to refresh enrollment status
			window.location.reload();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to join program';
			setJoinError(message);
			setIsJoining(false);
		}
	};

	return (
		<div className="leat-tiers-list__empty-state">
			<p>Join our loyalty program to unlock exclusive tier benefits.</p>
			<Button onClick={handleJoinProgram} disabled={isJoining} variant="contained">
				{isJoining ? 'Joining...' : attributes.buttonTextJoin}
			</Button>
			{joinError && <p className="leat-tiers-list__error">{joinError}</p>}
		</div>
	);
};

const EnrolledState = ({
	attributes,
	tiers,
	currentTierId,
	creditsCurrency,
	isGuest = false,
}: {
	attributes: Attributes;
	tiers: Tier[];
	currentTierId: string | null;
	creditsCurrency: string;
	isGuest?: boolean;
}) => {
	if (tiers.length === 0) {
		return (
			<div className="leat-tiers-list__empty-state">
				<p>No tier levels available at this time.</p>
			</div>
		);
	}

	// Simple button color extraction
	const buttonInlineStyles: React.CSSProperties = {};
	if (attributes.buttonTextColor) {
		buttonInlineStyles.color = attributes.buttonTextColor;
	}
	if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = attributes.buttonBackgroundColor;
	}

	// Create context value for all cards
	const contextValue = {
		currentTierId,
		creditsCurrency,
		labelLevel: attributes.cardLabelLevel,
		isGuest,
		showDescription: attributes.showDescription,
		buttonTexts: {
			join: attributes.buttonTextJoin,
			signIn: attributes.buttonTextSignIn,
			current: attributes.buttonTextCurrent,
		},
		buttonInlineStyles,
	};

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	return (
		<TiersListProvider value={contextValue}>
			<div className="leat-tiers-list__grid" style={gridStyle}>
				{tiers.map((tier) => (
					<TierCard
						key={tier.id}
						tier={tier}
						imageHeight={attributes.imageHeight}
						imageFit={attributes.imageFit}
						currentTierBorderColor={attributes.currentTierBorderColor}
					/>
				))}
			</div>
		</TiersListProvider>
	);
};

// ============================================================================
// Tier Card
// ============================================================================

const TierCard = ({
	tier,
	showJoinButton = false,
	imageHeight,
	imageFit,
	currentTierBorderColor,
}: {
	tier: Tier;
	showJoinButton?: boolean;
	imageHeight?: number;
	imageFit?: 'contain' | 'cover' | 'fill';
	currentTierBorderColor?: string;
}) => {
	// Get shared context instead of props
	const {
		currentTierId,
		labelLevel,
		isGuest,
		showDescription,
		buttonTexts,
		buttonInlineStyles,
		onJoinProgram,
		isJoiningProgram = false,
		joinProgramError,
	} = useTiersListContext();

	const isCurrent = currentTierId === tier.id;

	// Create dynamic heading tag based on labelLevel
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	// Generate redirect URL for login/join actions
	const myAccountUrl = window.leat?.storePages?.myaccount?.permalink || '/my-account/';
	const redirectUrl = `${myAccountUrl}?redirect_to=${encodeURIComponent(window.location.href)}`;

	// Render tier media (image, SVG, or emoji)
	const renderMedia = () => {
		if (!tier.media || !tier.media.value) {
			// Default icon if no media
			return <span style={{ fontSize: '48px' }}>🏆</span>;
		}

		// Handle image type (URL to an image file)
		if (tier.media.type === 'image' || tier.media.value.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
			return <img src={tier.media.value} alt={tier.name} />;
		}

		// Handle SVG inline content
		if (tier.media.type === 'svg') {
			return (
				<div
					// biome-ignore lint/security/noDangerouslySetInnerHtml: This is controlled SVG content from backend
					dangerouslySetInnerHTML={{ __html: tier.media.value }}
				/>
			);
		}

		// Handle emoji type
		if (tier.media.type === 'emoji') {
			return <span style={{ fontSize: '48px' }}>{tier.media.value}</span>;
		}

		// Fallback: if media.value exists but type is unknown, treat as image URL
		return <img src={tier.media.value} alt={tier.name} />;
	};

	return (
		<Item
			variant="default"
			className={isCurrent ? 'leat-tier-card--current' : ''}
			style={{
				'--leat-image-height': imageHeight ? `${imageHeight}px` : '200px',
				'--leat-image-fit': imageFit || 'cover',
				'--leat-current-tier-border-color': currentTierBorderColor || undefined,
			} as React.CSSProperties}
		>
			<ItemContent>
				<ItemMedia>
					{renderMedia()}
				</ItemMedia>
				<ItemLabel as={LabelTag}>{tier.name}</ItemLabel>
				{showDescription && tier.description && (
					<ItemMeta variant="description">
						{tier.description}
					</ItemMeta>
				)}
			</ItemContent>

			{/* Action zone: Always present for consistent card height */}
			<ItemActions>
				{showJoinButton ? (
					// Show Join Program button for non-enrolled users
					<>
						<Button
							onClick={onJoinProgram}
							disabled={isJoiningProgram}
							variant="contained"
							className="leat-button--small"
							style={buttonInlineStyles}
						>
							{isJoiningProgram ? 'Joining...' : buttonTexts.join}
						</Button>
						{joinProgramError && <ItemError>{joinProgramError}</ItemError>}
					</>
				) : isGuest ? (
					// Guest user - show sign in prompt
					<Button
						href={redirectUrl}
						variant="contained"
						className="leat-button--small"
						style={buttonInlineStyles}
					>
						{buttonTexts.signIn}
					</Button>
				) : isCurrent ? (
					// Show current tier indicator
					<Button
						renderAs="span"
						variant="text"
						className="leat-button--small leat-button--current"
						removeTextWrap
						style={buttonInlineStyles}
					>
						<CheckCircleIcon />
						<span>{buttonTexts.current}</span>
					</Button>
				) : null}
			</ItemActions>
		</Item>
	);
};

// ============================================================================
// Helpers
// ============================================================================

const CheckCircleIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="12" r="10" />
		<path d="M9 12l2 2 4-4" />
	</svg>
);

// ============================================================================
// Initialization
// ============================================================================

export function initTiersListBlocks() {
	// Hydrate ALL blocks regardless of enrollment status
	const blocks = document.querySelectorAll<HTMLElement>('.leat-tiers-list');

	for (const block of blocks) {
		const attributes: Attributes = {
			columns: Number(block.dataset.columns) || 3,
			cardLabelLevel: Number(block.dataset.cardLabelLevel) || 4,
			buttonTextJoin: block.dataset.buttonTextJoin || 'Join Program',
			buttonTextSignIn: block.dataset.buttonTextSignIn || 'Sign In to Join',
			buttonTextCurrent: block.dataset.buttonTextCurrent || 'Current Tier',
			showDescription: block.dataset.showDescription !== 'false', // Default to true
			imageHeight: Number(block.dataset.imageHeight) || 200,
			imageFit: (block.dataset.imageFit as 'contain' | 'cover' | 'fill') || 'cover',
			buttonTextColor: block.dataset.buttonTextColor && block.dataset.buttonTextColor !== '' ? block.dataset.buttonTextColor : undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor && block.dataset.buttonBackgroundColor !== '' ? block.dataset.buttonBackgroundColor : undefined,
			currentTierBorderColor: block.dataset.currentTierBorderColor && block.dataset.currentTierBorderColor !== '' ? block.dataset.currentTierBorderColor : undefined,
		};

		createRoot(block).render(<TiersListView attributes={attributes} />);
	}
}

// Auto-initialize
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initTiersListBlocks);
} else {
	initTiersListBlocks();
}
